import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS, TYPOGRAPHY } from '../../../../constants/interaxColors'
import { SprintActivityItemForGamerDashboard } from '../../../../interaxDesignSystemComponents'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { SprintActivity } from '../../../../../../pure-js/types/Sprint'
import dayjs from 'dayjs'

export interface SprintActivitiesListProps {
  activities: SprintActivity[]
  userTeamId?: string
  onActivityClick?: () => void
  className?: string
}

/**
 * SprintActivitiesList - Grid of sprint activities
 * Displays activities with completion and in-progress status
 */
export const SprintActivitiesList: React.FC<SprintActivitiesListProps> = ({
  activities,
  userTeamId,
  onActivityClick,
  className = ''
}) => {
  const { t } = useTranslation()

  // Check activity status
  const getActivityStatus = (activity: SprintActivity) => {
    // Ended timed activities (intro/reflection) complete for every team even if not listed per-team.
    const isCompleted =
      !!activity.endedAt || (userTeamId ? (activity.completedActivityTeamIds || []).includes(userTeamId) : false)
    const now = dayjs()
    const start = dayjs(activity.startDate)
    const end = dayjs(activity.endDate)
    const isInProgress = now.isAfter(start) && now.isBefore(end)

    return { isCompleted, isInProgress }
  }

  if (!activities || activities.length === 0) {
    return (
      <Box
        className={className}
        sx={{
          textAlign: 'center',
          paddingY: 4,
          color: INTERAX_COLORS.thunderGrey
        }}
      >
        <Box
          component="p"
          sx={{
            ...TYPOGRAPHY.body2
          }}
        >
          {t('gamer.sprintProgress.noActivities', 'No activities in this sprint')}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      className={className}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)'
        },
        gap: 2
      }}
    >
      {activities.map((activity) => {
        const { isCompleted, isInProgress } = getActivityStatus(activity)
        return (
          <SprintActivityItemForGamerDashboard
            key={activity.id}
            activity={activity}
            isCompleted={isCompleted}
            isInProgress={isInProgress}
            onClick={onActivityClick}
          />
        )
      })}
    </Box>
  )
}
